import { useState, useEffect } from "react";
import { 
    Stack, 
    Dialog, 
    DialogType, 
    DialogFooter, 
    Spinner, 
    SpinnerSize, 
    DefaultButton, 
    PrimaryButton,
    TextField,
    Label,
    mergeStyles,
    Icon
} from "@fluentui/react";
import { 
    getPromptTemplates,
    updatePromptCategory,
    deletePromptCategory,
    swapPromptCategoryOrder,
    swapPromptTemplateOrder,
    deletePromptTemplate
} from "../../../api/admin/api_admin";
import { 
    PromptTemplatesResponse, 
    PromptCategoryWithTemplates,
    PromptTemplate
} from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton, ConfirmDialog } from "../components";

const accordionHeaderStyle = mergeStyles({
    padding: '12px 16px',
    backgroundColor: '#f3f2f1',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '4px',
    marginBottom: '8px',
    ':hover': {
        backgroundColor: '#e1dfdd'
    }
});

const accordionPanelStyle = mergeStyles({
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '4px',
    marginBottom: '8px',
    border: '1px solid #e1dfdd'
});

export const PromptTemplates = () => {
    const [data, setData] = useState<PromptTemplatesResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    
    // アコーディオンの開閉状態
    const [expandedCategories, setExpandedCategories] = useState<{ [key: number]: boolean }>({});
    const [expandedUnclassified, setExpandedUnclassified] = useState<boolean>(false);
    
    // カテゴリ名変更用
    const [renamingCategoryId, setRenamingCategoryId] = useState<number | null>(null);
    const [newCategoryName, setNewCategoryName] = useState<string>("");

    // カテゴリ削除用
    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [categoryToDelete, setCategoryToDelete] = useState<PromptCategoryWithTemplates | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    // テンプレート削除用
    const [deleteTemplateDialogVisible, setDeleteTemplateDialogVisible] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<PromptTemplate | null>(null);
    const [deletingTemplate, setDeletingTemplate] = useState<boolean>(false);

    useEffect(() => {
        fetchPromptTemplates();
    }, []);

    const expandAll = () => {
        if (!data) return;
        const category_ids = data.categories.map(v => v.id);
        const expanded: { [key: number]: boolean } = {};
        category_ids.forEach(id => {
            expanded[id] = true;
        });
        setExpandedCategories(expanded);
        setExpandedUnclassified(true);
    };

    const collapseAll = () => {
        setExpandedCategories({});
        setExpandedUnclassified(false);
    };
    
    const toggleCategory = (categoryId: number) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const fetchPromptTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getPromptTemplates();
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const canAddCategory = (): boolean => {
        if (!data) return false;
        const addFunc = data.functions['add_prompt_templates'];
        return addFunc && data.current_user.authority_level >= addFunc.need_authority_level;
    };

    const canModify = (): boolean => {
        if (!data) return false;
        const modifyFunc = data.functions['modify_prompt_templates'];
        return modifyFunc && data.current_user.authority_level >= modifyFunc.need_authority_level;
    };

    const canDelete = (): boolean => {
        if (!data) return false;
        const deleteFunc = data.functions['delete_prompt_templates'];
        return deleteFunc && data.current_user.authority_level >= deleteFunc.need_authority_level;
    };

    const handleCategoryMoveUp = async (category: PromptCategoryWithTemplates, isFirst: boolean) => {
        if (isFirst || !category.company_cd) return;
        
        try {
            const result = await swapPromptCategoryOrder({ id: category.id, mode: 'swapup' });
            if (result.success) {
                await fetchPromptTemplates();
            } else {
                setError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            setError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleCategoryMoveDown = async (category: PromptCategoryWithTemplates, isLast: boolean) => {
        if (isLast || !category.company_cd) return;
        
        try {
            const result = await swapPromptCategoryOrder({ id: category.id, mode: 'swapdown' });
            if (result.success) {
                await fetchPromptTemplates();
            } else {
                setError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            setError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleRenameCategoryClick = (category: PromptCategoryWithTemplates) => {
        setRenamingCategoryId(category.id);
        setNewCategoryName(category.name);
    };

    const handleRenameCategorySubmit = async (categoryId: number) => {
        if (!newCategoryName.trim()) {
            setError("カテゴリ名を入力してください");
            return;
        }

        try {
            const result = await updatePromptCategory({ 
                category_id: categoryId, 
                name: newCategoryName 
            });
            if (result.success) {
                setSuccessMessage("カテゴリ名の変更に成功しました");
                setRenamingCategoryId(null);
                await fetchPromptTemplates();
            } else {
                setError(result.message || "カテゴリ名の変更に失敗しました");
            }
        } catch (err) {
            setError("カテゴリ名の変更中にエラーが発生しました");
        }
    };

    const handleDeleteCategoryClick = (category: PromptCategoryWithTemplates) => {
        setCategoryToDelete(category);
        setDeleteDialogVisible(true);
    };

    const handleDeleteCategoryConfirm = async () => {
        if (!categoryToDelete) return;
        
        setDeleting(true);
        try {
            const result = await deletePromptCategory({ category_id: categoryToDelete.id });
            if (result.success) {
                setSuccessMessage("カテゴリの削除に成功しました");
                setDeleteDialogVisible(false);
                setCategoryToDelete(null);
                await fetchPromptTemplates();
            } else {
                setError(result.message || "削除に失敗しました");
                setDeleteDialogVisible(false);
            }
        } catch (err) {
            setError("削除中にエラーが発生しました");
            setDeleteDialogVisible(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCategoryCancel = () => {
        setDeleteDialogVisible(false);
        setCategoryToDelete(null);
    };

    const handleTemplateMoveUp = async (template: PromptTemplate, isFirst: boolean) => {
        if (isFirst || !template.is_modifiable) return;
        
        try {
            const result = await swapPromptTemplateOrder({ id: template.id, mode: 'swapup' });
            if (result.success) {
                await fetchPromptTemplates();
            } else {
                setError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            setError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleTemplateMoveDown = async (template: PromptTemplate, isLast: boolean) => {
        if (isLast || !template.is_modifiable) return;
        
        try {
            const result = await swapPromptTemplateOrder({ id: template.id, mode: 'swapdown' });
            if (result.success) {
                await fetchPromptTemplates();
            } else {
                setError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            setError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleDeleteTemplateClick = (template: PromptTemplate) => {
        setTemplateToDelete(template);
        setDeleteTemplateDialogVisible(true);
    };

    const handleDeleteTemplateConfirm = async () => {
        if (!templateToDelete) return;
        
        setDeletingTemplate(true);
        try {
            const result = await deletePromptTemplate({ template_id: templateToDelete.id });
            if (result.success) {
                setSuccessMessage("テンプレートの削除に成功しました");
                setDeleteTemplateDialogVisible(false);
                setTemplateToDelete(null);
                await fetchPromptTemplates();
            } else {
                setError(result.message || "削除に失敗しました");
                setDeleteTemplateDialogVisible(false);
            }
        } catch (err) {
            setError("削除中にエラーが発生しました");
            setDeleteTemplateDialogVisible(false);
        } finally {
            setDeletingTemplate(false);
        }
    };

    const handleDeleteTemplateCancel = () => {
        setDeleteTemplateDialogVisible(false);
        setTemplateToDelete(null);
    };

    const renderTemplateTable = (templates: PromptTemplate[], categoryId: number | null) => {
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f3f2f1' }}>
                        <th style={{ border: '1px solid #e1dfdd', padding: '8px', width: '80px' }}>操作</th>
                        <th style={{ border: '1px solid #e1dfdd', padding: '8px', width: '150px' }}>アクション</th>
                        <th style={{ border: '1px solid #e1dfdd', padding: '8px' }}>テンプレート名</th>
                        <th style={{ border: '1px solid #e1dfdd', padding: '8px' }}>概要</th>
                        <th style={{ border: '1px solid #e1dfdd', padding: '8px', width: '200px' }}>グループ</th>
                    </tr>
                </thead>
                <tbody>
                    {templates.map((template, index) => (
                        <tr key={template.id}>
                            <td style={{ border: '1px solid #e1dfdd', padding: '8px', textAlign: 'center' }}>
                                {template.is_modifiable && canModify() && (
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        <DefaultButton 
                                            text="上へ"
                                            onClick={() => handleTemplateMoveUp(template, index === 0)}
                                            disabled={index === 0}
                                            styles={{ 
                                                root: { 
                                                    width: '60px', 
                                                    height: '24px',
                                                    minHeight: '24px',
                                                    padding: '0 4px',
                                                    fontSize: '12px'
                                                }
                                            }}
                                        />
                                        <DefaultButton 
                                            text="下へ"
                                            onClick={() => handleTemplateMoveDown(template, index === templates.length - 1)}
                                            disabled={index === templates.length - 1}
                                            styles={{ 
                                                root: { 
                                                    width: '60px', 
                                                    height: '24px',
                                                    minHeight: '24px',
                                                    padding: '0 4px',
                                                    fontSize: '12px'
                                                }
                                            }}
                                        />
                                    </Stack>
                                )}
                            </td>
                            <td style={{ border: '1px solid #e1dfdd', padding: '8px' }}>
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                    {template.is_modifiable && canModify() && (
                                        <LinkButton 
                                            link_to={`/edit_prompt_template/${template.id}`} 
                                            text="編集"
                                            external
                                        />
                                    )}
                                    {template.is_modifiable && canDelete() && (
                                        <DefaultButton 
                                            text="削除"
                                            onClick={() => handleDeleteTemplateClick(template)}
                                        />
                                    )}
                                    {canAddCategory() && (
                                        <LinkButton 
                                            link_to={`/edit_prompt_template?copy_from=${template.id}`} 
                                            text="複製"
                                            external
                                        />
                                    )}
                                </Stack>
                            </td>
                            <td style={{ border: '1px solid #e1dfdd', padding: '8px' }}>{template.name}</td>
                            <td style={{ border: '1px solid #e1dfdd', padding: '8px' }}>{template.description}</td>
                            <td style={{ border: '1px solid #e1dfdd', padding: '8px' }}>{template.groups}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const reorderableCategoryIds = data?.categories.filter((c) => c.company_cd !== null).map((c) => c.id)
    const renderCategorySection = (category: PromptCategoryWithTemplates, index: number) => {
        const isExpanded = expandedCategories[category.id] || false;
        const templates = data?.templates[category.id] || [];
        const isFirst = category.id == reorderableCategoryIds?.at(0);
        const isLast = category.id == reorderableCategoryIds?.at(reorderableCategoryIds.length - 1);

        return (
            <div key={category.id} style={{ marginBottom: '8px' }} data-testid={`prompt-category-${category.id}`}>
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                    {/* 並び順変更ボタン */}
                    {category.company_cd !== null && canModify() ? (
                        <Stack verticalAlign="center" styles={{ root: { width: '50px' } }}>
                            <DefaultButton 
                                text="上へ"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategoryMoveUp(category, isFirst);
                                }}
                                disabled={isFirst}
                                styles={{ 
                                    root: { 
                                        width: '50px', 
                                        height: '22px',
                                        minHeight: '22px',
                                        minWidth: '50px',
                                        padding: '0 4px',
                                        fontSize: '11px'
                                    }
                                }}
                            />
                            <DefaultButton 
                                text="下へ"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategoryMoveDown(category, isLast);
                                }}
                                disabled={isLast}
                                styles={{ 
                                    root: { 
                                        width: '50px', 
                                        height: '22px',
                                        minHeight: '22px',
                                        minWidth: '50px',
                                        padding: '0 4px',
                                        fontSize: '12px',
                                        marginTop: '2px'
                                    }
                                }}
                            />
                        </Stack>
                    ) : (
                        <div style={{ width: '50px' }} />
                    )}
                    
                    {/* アコーディオンヘッダー */}
                    <div 
                        className={accordionHeaderStyle} 
                        onClick={() => toggleCategory(category.id)}
                        style={{ flex: 1 }}
                    >
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }} styles={{ root: { width: '100%' } }}>
                            <Stack.Item>
                                <Icon iconName={isExpanded ? "ChevronDown" : "ChevronRight"} />
                            </Stack.Item>
                            <Stack.Item>
                                <Label styles={{ root: { fontWeight: 600 } }}>{category.name}</Label>
                            </Stack.Item>
                        </Stack>
                    </div>
                </Stack>
                
                {isExpanded && (
                    <div className={accordionPanelStyle}>
                        <Stack tokens={{ childrenGap: 12 }}>
                            {/* カテゴリ編集 */}
                            {category.company_cd !== null && (
                                <Stack tokens={{ childrenGap: 12 }}>
                                    <Label styles={{ root: { fontWeight: 600 } }}>カテゴリ編集</Label>
                                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                                        {canModify() && (
                                            <>
                                                <TextField
                                                    value={renamingCategoryId === category.id ? newCategoryName : category.name}
                                                    onChange={(_, newValue) => {
                                                        if (renamingCategoryId === category.id) {
                                                            setNewCategoryName(newValue || "");
                                                        }
                                                    }}
                                                    onFocus={() => handleRenameCategoryClick(category)}
                                                    placeholder="新しいカテゴリ名"
                                                    styles={{ root: { width: 300 } }}
                                                    autoComplete="off"
                                                />
                                                <PrimaryButton 
                                                    text="名前の変更" 
                                                    onClick={() => handleRenameCategorySubmit(category.id)}
                                                    disabled={renamingCategoryId !== category.id || newCategoryName === category.name}
                                                />
                                            </>
                                        )}
                                        {canDelete() && (
                                            <DefaultButton 
                                                text="削除する" 
                                                onClick={() => handleDeleteCategoryClick(category)}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            )}

                            {/* テンプレート一覧 */}
                            <Stack tokens={{ childrenGap: 8 }}>
                                <Stack verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                    <Label styles={{ root: { fontWeight: 600 } }}>テンプレート一覧</Label>
                                    {canAddCategory() && (
                                        <LinkButton 
                                            link_to={`/edit_prompt_template?category_id=${category.id}`} 
                                            text="新規テンプレート作成"
                                            external
                                        />
                                    )}
                                </Stack>
                                {renderTemplateTable(templates, category.id)}
                            </Stack>
                        </Stack>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="カテゴリ別プロンプトテンプレート一覧" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog 
                    content={successMessage ?? ""} 
                    onDismiss={() => setSuccessMessage(null)} 
                    isOpen={Boolean(successMessage)}
                    title="確認" />
            }

            {/* アクションボタン */}
            <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="space-between" styles={{ root: { width: '100%' } }}>
                <Stack.Item>
                    {canAddCategory() && (
                        <LinkButton link_to="/menu/prompt_templates_list/add_category" text="新規カテゴリ作成" />
                    )}
                </Stack.Item>
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                    <DefaultButton text="すべてのカテゴリを展開" onClick={expandAll} />
                    <DefaultButton text="すべてのカテゴリを閉じる" onClick={collapseAll} />
                </Stack>
            </Stack>

            {/* カテゴリ一覧 */}
            {data && data.categories.length > 0 ? (
                <div>
                    {data.categories.map((category, index) => renderCategorySection(category, index))}
                </div>
            ) : (
                <Label>カテゴリがありません</Label>
            )}

            {/* 未分類 */}
            {data && data.unclassified.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <div style={{ width: '50px' }} />
                        <div 
                            className={accordionHeaderStyle} 
                            onClick={() => setExpandedUnclassified(!expandedUnclassified)}
                            style={{ flex: 1 }}
                        >
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }} styles={{ root: { width: '100%' } }}>
                                <Stack.Item>
                                    <Icon iconName={expandedUnclassified ? "ChevronDown" : "ChevronRight"} />
                                </Stack.Item>
                                <Stack.Item>
                                    <Label styles={{ root: { fontWeight: 600 } }}>未分類</Label>
                                </Stack.Item>
                            </Stack>
                        </div>
                    </Stack>
                    
                    {expandedUnclassified && (
                        <div className={accordionPanelStyle}>
                            <Stack tokens={{ childrenGap: 8 }}>
                                <Label styles={{ root: { fontWeight: 600 } }}>テンプレート一覧</Label>
                                {renderTemplateTable(data.unclassified, null)}
                            </Stack>
                        </div>
                    )}
                </div>
            )}

            {/* カテゴリ削除確認ダイアログ */}
            <Dialog
                hidden={!deleteDialogVisible}
                onDismiss={handleDeleteCategoryCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '削除確認',
                    subText: `カテゴリ「${categoryToDelete?.name}」を削除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteCategoryConfirm} text="削除" disabled={deleting} />
                    <DefaultButton onClick={handleDeleteCategoryCancel} text="キャンセル" disabled={deleting} />
                </DialogFooter>
            </Dialog>

            {/* テンプレート削除確認ダイアログ */}
            <Dialog
                hidden={!deleteTemplateDialogVisible}
                onDismiss={handleDeleteTemplateCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '削除確認',
                    subText: `テンプレート「${templateToDelete?.name}」を削除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteTemplateConfirm} text="削除" disabled={deletingTemplate} />
                    <DefaultButton onClick={handleDeleteTemplateCancel} text="キャンセル" disabled={deletingTemplate} />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
};
